import React, { FC } from "react";
import { Flex, Text, Title, Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";

import cx from "./customer_information.module.css";

import { AccountCreateLambdaResponse } from "src/services/accountActivation/accountActivationTypes";

/**
 *
 */

export const CardWrapper = React.memo<{
  title: string;
  children: React.ReactNode;
  "data-testid": string;
}>(({ title, children, "data-testid": dataTestId }) => {
  return (
    <Flex
      mt="lg"
      direction="column"
      data-testid={dataTestId}
      className={cx["customer-information-card"]}
    >
      <Title data-testid={`${dataTestId}-title`}>{title}</Title>
      {children}
    </Flex>
  );
});

CardWrapper.displayName = "CardWrapper";

/**
 * Address
 */

type ShopifyAddr = NonNullable<
  AccountCreateLambdaResponse["shopifyOrder"]["shipping_address"]
>;

const Address = React.memo<{
  title: string;
  address: ShopifyAddr;
  "data-testid": string;
}>(function Address(props) {
  const { address, title, "data-testid": dataTestId } = props;

  return (
    <div data-testid={dataTestId}>
      <Text fw={700}>{title}</Text>
      <Text fw={300} data-testid={`${dataTestId}-name`}>
        {address.name}
      </Text>
      <Text fw={300} data-testid={`${dataTestId}-addr1`}>
        {address.address1}
      </Text>
      <Text fw={300} data-testid={`${dataTestId}-addr2`}>
        {address.address2}
      </Text>
      <Text fw={300} data-testid={`${dataTestId}-city-state-zip`}>
        {address.city}, {address.province_code} {address.zip}
      </Text>
      <Text data-testid={`${dataTestId}-country`}>{address.country_code}</Text>
    </div>
  );
});

/**
 *
 */

interface CustomerInformationProps {
  accountCreateResp: AccountCreateLambdaResponse;
  showBillingAddress: boolean;
}

const RIGHT_COLUMN_WIDTH = 5;
const LEFT_COLUMN_WDITH = 12 - RIGHT_COLUMN_WIDTH;

export const CustomerInformation: FC<CustomerInformationProps> = (props) => {
  const { t } = useTranslation();

  const { accountCreateResp } = props;

  const user = accountCreateResp.order.user;

  const billingAddrShopify = accountCreateResp.shopifyOrder.billing_address;
  const shippingAddrShopify = accountCreateResp.shopifyOrder.shipping_address;

  return (
    <CardWrapper
      title={t("startPage.customerInformation")}
      data-testid="customer-info-card"
    >
      <Grid>
        <Grid.Col
          span={{ sm: 12, md: LEFT_COLUMN_WDITH }}
          style={{ overflowWrap: "break-word" }}
        >
          <Text fw={700}>{t("startPage.contactInformation")}</Text>
          <Text fw={300}>{user.email}</Text>
          <Text fw={300}>{user.phonenumber}</Text>
        </Grid.Col>

        {props.showBillingAddress || !shippingAddrShopify ? (
          <Grid.Col
            span={{ sm: 12, md: RIGHT_COLUMN_WIDTH }}
            style={{ overflowWrap: "break-word" }}
          >
            <Address
              address={billingAddrShopify}
              title={t("startPage.billingAddress")}
              data-testid="billing-addr"
            />
          </Grid.Col>
        ) : (
          // otherwise, just empty column lol
          <Grid.Col span={{ sm: 12, md: RIGHT_COLUMN_WIDTH }} />
        )}

        {shippingAddrShopify ? (
          <Grid.Col
            span={{ sm: 12, md: LEFT_COLUMN_WDITH }}
            style={{ overflowWrap: "break-word" }}
          >
            <Address
              address={shippingAddrShopify}
              title={t("startPage.shippingAddress")}
              data-testid="shipping-addr"
            />
          </Grid.Col>
        ) : null}
      </Grid>
    </CardWrapper>
  );
};
